import { mergeStyles, PrimaryButton, ProgressIndicator } from 'office-ui-fabric-react';
import * as React from 'react';

import { ComponentOverrides, LogEventName, LogLevel } from '../../models';
import { DiagnosticsExecutionStatus, SafConstants } from '../../models/safDiagnostic.models';
import { createStructuredSelector, mconnect } from '../../state/redux';
import { IStyledComponent, styledComponent } from '../../theme';
import { IComponentStateProps, IConnectedComponentProps, ISafInlineDiagnosticsStyles } from '../../types';
import { generateAdhocs, truncateWithEllipsis } from '../../utils';
import * as strings from '../intentGraph.string.json';
import { getsafDiagnosticsClassNames, getSafDiagnosticsStyles } from '../intentGraph.styles';
import { resetDiagnosticsRunStatusAction, setDiagnosticsStatusAction } from '../reducers/actions';
import { dispatchDiagnosticsFormComponentUpdate, dispatchStartDiagnostics, dispatchStartSAFAction } from '../reducers/dispatchers';
import {
  diagnosticsFormSelector,
  diagnosticsMaxRetriesSelector,
  diagnosticsProgressSelectror,
  diagnosticsResponseSelector,
  getNodeSelector,
  jsonPayloadMetaSelector,
  sessionIdSelector,
  staticConfigSelector
} from '../reducers/selectors.redux';

import { getCommonClassNames } from './common/common.styles';
import { FormBuilder } from './common/FormBuilder';
import { ComponentOverride } from './common/ComponentOverride';

const mapStateToProps = createStructuredSelector({
  diagnosticsForm: diagnosticsFormSelector,
  diagnosticsResponse: diagnosticsResponseSelector,
  diagnosticsProgress: diagnosticsProgressSelectror,
  node: getNodeSelector,
  sessionId: sessionIdSelector,
  meta: jsonPayloadMetaSelector,
  staticConfig: staticConfigSelector,
  maxIteration: diagnosticsMaxRetriesSelector
});

const mapDispatchToProps = {
  dispatchDiagnosticsFormComponentUpdate,
  dispatchStartDiagnostics,
  dispatchStartSAFAction,
  resetDiagnosticsRunStatusAction,
  setDiagnosticsStatusAction
};

const enum DiagnosticsViewMode {
  Form,
  Progress,
  Result,
  Failed,
  ContinueWithNext
}

interface ISAFInlineDiagnosticsProps {
  isSAFAction?: boolean;
}

type IInlineDiagnosticsProps = ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps &
  IStyledComponent<ISafInlineDiagnosticsStyles> &
  IConnectedComponentProps &
  IComponentStateProps &
  ISAFInlineDiagnosticsProps;

class SafInlineDiagnosticsC extends React.PureComponent<IInlineDiagnosticsProps> {
  private sectionClassNames = getsafDiagnosticsClassNames(this.props.styles, {
    theme: this.props.theme,
    stylesFunc: this.props.stylesFunc
  });
  private commonClassNames = getCommonClassNames(this.props.styles, {
    theme: this.props.theme,
    stylesFunc: this.props.stylesFunc
  });
  private defaultBtnLabel = strings.Diagnostics.SubmitButtonLabel;

  public render(): React.ReactNode {
    if (!this.props.diagnosticsForm) {
      return <></>;
    }
    switch (this._getDiagnosticsViewMode()) {
      case DiagnosticsViewMode.Form:
        return this._getDiagnosticsForm();
      case DiagnosticsViewMode.Progress:
        let showProgress = true;
        let showProgressString = true;
        const diagnosticsSettings = this.props.staticConfig.get('safDiagnosticsSettings');
        if(diagnosticsSettings){
          showProgress = !diagnosticsSettings.get('hideProgressBar')
          showProgressString = !diagnosticsSettings.get('hideProgressStrings')
        }
        const ProgressBar = this.props.callbackHandlers.GetComponent && this.props.callbackHandlers.GetComponent(ComponentOverrides.DiagnosticProgressBar)
        if(ProgressBar){
          <ComponentOverride componentName={ComponentOverrides.DiagnosticProgressBar} callbackHandlers={this.props.callbackHandlers}/>
        }
        return (
          <>
            {showProgressString && <>
              <div className={this.sectionClassNames.contentContainer}>
              </div>
            </> }
            {showProgress && <div className={this.sectionClassNames.contentContainer}>
              <ProgressIndicator/>
            </div>}
          </>
        );
      case DiagnosticsViewMode.Failed:
        let messageTitle = strings.Diagnostics.DiagnosticsRunFailedTitle;
        if (this.props.diagnosticsProgress === this.props.maxIteration) {
          messageTitle = strings.Diagnostics.DiagnosticsRunTimeoutTitle;
        }
        return <>{this._renderRunCompleteView(DiagnosticsViewMode.Failed, messageTitle)}</>;
      case DiagnosticsViewMode.Result:
        const resultMessageTitle = strings.Diagnostics.DiagnosticsRunCompleted;
        return <>{this._renderRunCompleteView(DiagnosticsViewMode.Result, resultMessageTitle)}</>;
      case DiagnosticsViewMode.ContinueWithNext:
        return <>{this._getContinueWithNext()}</>;
    }
  }

  private _getDiagnosticsTitleAndDetail = (): React.ReactNode => {
    const title = this.props.diagnosticsResponse.get('Title');
    // SearchHelpConstants.DiagnosticsTitleStringLimit, add to constants
    const truncatedTitle = truncateWithEllipsis(title, SafConstants.DiagnosticsTitleStringLimit);
    return (
      <>
        {!!title && (
          <>
            <div className={mergeStyles(this.sectionClassNames.title, this.sectionClassNames.contentContainer)}>
              <div title={title}>{truncatedTitle}</div>
            </div>
          </>
        )}
        {this.props.diagnosticsResponse.get('Details') && (
          <div
            className={this.sectionClassNames.contentContainer}
            dangerouslySetInnerHTML={this._getDiagnosticsDetails()}  // eslint-disable-line
          />
        )}
      </>
    );
  };

  private _getContinueWithNext = (): React.ReactNode => {
    return (
      <>
        {this._getDiagnosticsTitleAndDetail()}
        {this._getDiagnosticsForm()}
      </>
    );
  };

  private _getDiagnosticsDetails = () => {
    return {
      __html: this.props.diagnosticsResponse.get('Details')
    };
  };

  private _getDiagnosticsForm = (): React.ReactNode => {
    const btnLabel =
      !!this.props.diagnosticsForm && !!this.props.diagnosticsForm.get('ButtonLabel')
        ? this.props.diagnosticsForm.get('ButtonLabel')
        : this.defaultBtnLabel;
    return (
      <>
        <FormBuilder
          stylesFunc={this.props.stylesFunc}
          callbackHandlers={this.props.callbackHandlers}
          key={this.props.diagnosticsForm.get('Key')}
          formSchema={this.props.diagnosticsForm}
          submitBtnLabel={btnLabel}
          formComponentReducer={this.props.dispatchDiagnosticsFormComponentUpdate}
          formSubmitReducer={this._onFormSubmit}
        />
      </>
    );
  };

  private _onFormSubmit = () => {
    if(!this.props.isSAFAction){
      this.props.dispatchStartDiagnostics(
        this.props.callbackHandlers.StartDiagnostics,
        this.props.callbackHandlers.GetDiagnosticsResult,
        this.props.callbackHandlers.LogHandler,
        this.props.node.get('uId'),
        this.props.node.get('dialogId')
      );
    }
    else{
      this.props.dispatchStartSAFAction(
        this.props.callbackHandlers.ExecuteSAFAction,
        this.props.callbackHandlers.LogHandler,
        this.props.node
      );
    }

    this.props.callbackHandlers.LogHandler(
      LogEventName.onStartDiagnosticsRun,
      LogLevel.Info,
      this.props.sessionId,
      `StartDiagnostics runs.`,
      generateAdhocs(this.props.meta, undefined, undefined, this.props.node)
    );
  };

  private _getDiagnosticsViewMode = (): DiagnosticsViewMode => {
    if (this.props.diagnosticsResponse) {
      if (this.props.diagnosticsResponse.size <= 0) {
        return DiagnosticsViewMode.Form;
      }
      switch (this.props.diagnosticsResponse.get('Status')) {
        case DiagnosticsExecutionStatus.Executing:
        case DiagnosticsExecutionStatus.StartRequested:
          return DiagnosticsViewMode.Progress;
        case DiagnosticsExecutionStatus.Failed:
          return DiagnosticsViewMode.Failed;
        case DiagnosticsExecutionStatus.Completed:
          return DiagnosticsViewMode.Result;
        case DiagnosticsExecutionStatus.ContinueWithNext:
          return DiagnosticsViewMode.ContinueWithNext;
      }
    }
    return DiagnosticsViewMode.Form;
  };

  private _renderRunCompleteView = (
    viewMode: DiagnosticsViewMode,
    title?: string,
    description?: string
  ): React.ReactNode => {
    if(this.props.isSAFAction){
      return null;
    }
    return (
      <>
        {viewMode === DiagnosticsViewMode.Failed && (
          <>
            {title && (
              <>
                <div
                  className={mergeStyles(this.commonClassNames.sectionHeader, this.sectionClassNames.contentContainer)}
                >
                  {title}
                </div>
              </>
            )}
            {description && <div className={this.sectionClassNames.contentContainer}>{description}</div>}
            <div className={this.sectionClassNames.sectionContainer}>
              <PrimaryButton
                text={strings.Diagnostics.RerunTestButtonLabel}
                onClick={this._onRerunClick}
                autoFocus={true}
              />
            </div>
          </>
        )}
        {viewMode === DiagnosticsViewMode.Result && <>{this._getDiagnosticsTitleAndDetail()}</>}
      </>
    );
  };

  private _onRerunClick = () => {
    this.props.resetDiagnosticsRunStatusAction();
    this.props.callbackHandlers.LogHandler(
      LogEventName.onDiagnosticsRerun,
      LogLevel.Info,
      this.props.sessionId,
      'Diagnostics rerun tests.',
      generateAdhocs(this.props.meta, undefined, undefined, this.props.node)
    );
  };
}

export const SafInlineDiagnostics = mconnect(
  mapStateToProps,
  mapDispatchToProps
)(styledComponent(SafInlineDiagnosticsC, getSafDiagnosticsStyles));

